import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, ShoppingBag, CheckCircle } from "lucide-react";
import { useCart } from "../context/CartContext";
import { API, DELIVERY_OPTIONS } from "../data/content";

export default function CartDrawer() {
  const { items, setQty, clear, total, open, setOpen } = useCart();
  const [form, setForm] = useState({ customer_name: "", phone: "", email: "", delivery: "", notes: "" });
  const [placing, setPlacing] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const [shipCity, setShipCity] = useState("");
  const [shipPostal, setShipPostal] = useState("");
  const [rates, setRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState("");
  const [inspo, setInspo] = useState(null);
  const [inspoPreview, setInspoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const hasCupcakes = items.some((i) => i.id === "cupcakes");

  const uploadInspo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await axios.post(`${API}/uploads`, data);
      setInspo(res.data.path);
      setInspoPreview(URL.createObjectURL(file));
    } catch (err) {
      setError(err.response?.data?.detail || "Could not upload the image — you can also send it on WhatsApp.");
    } finally {
      setUploading(false);
    }
  };

  const isBobgo = form.delivery.startsWith("Bobgo");
  const grandTotal = total + (selectedRate?.amount ?? 0);

  const getQuote = async () => {
    setQuoteError("");
    setRates([]);
    setSelectedRate(null);
    setQuoting(true);
    try {
      const { data } = await axios.post(`${API}/shipping/quote`, {
        city: shipCity,
        postal_code: shipPostal,
        items: items.map((i) => ({ product_id: i.id, option: i.option, quantity: i.qty })),
      });
      setRates(data.rates);
    } catch (err) {
      setQuoteError(err.response?.data?.detail || "Could not get courier rates right now — we'll confirm the cost on WhatsApp.");
    } finally {
      setQuoting(false);
    }
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    setError("");
    setPlacing(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        ...form,
        inspo,
        shipping: selectedRate ? { label: selectedRate.label, amount: selectedRate.amount } : null,
        items: items.map((i) => ({ product_id: i.id, option: i.option, selections: i.selections, quantity: i.qty })),
      });
      setDone(data);
      clear();
      window.open(data.whatsapp_url, "_blank");
    } catch {
      setError("Something went wrong placing your order. Please try again or WhatsApp us directly.");
    } finally {
      setPlacing(false);
    }
  };

  const close = () => { setOpen(false); setTimeout(() => setDone(null), 400); };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      onClick={close} className="fixed inset-0 bg-black/70 z-[60]" data-testid="cart-overlay" />
          <motion.aside initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ ease: [0.76, 0, 0.24, 1], duration: 0.5 }}
                        data-testid="cart-drawer"
                        className="fixed right-0 top-0 h-full w-full max-w-md bg-[#0A0A0A] border-l border-[#D4AF37]/30 z-[70] flex flex-col">
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#F9F9F6]/10">
              <h3 className="font-serif-e text-2xl text-[#F9F9F6] flex items-center gap-3">
                <ShoppingBag size={18} strokeWidth={1.5} className="text-[#D4AF37]" /> Your Order
              </h3>
              <button onClick={close} data-testid="cart-close-btn" aria-label="Close cart" className="text-[#F9F9F6]/60 hover:text-[#D4AF37] transition-colors">
                <X size={22} strokeWidth={1.5} />
              </button>
            </div>

            {done ? (
              <div className="flex-1 flex flex-col items-center justify-center px-8 text-center" data-testid="order-success">
                <CheckCircle size={44} strokeWidth={1} className="text-[#D4AF37]" />
                <h4 className="font-serif-e text-3xl text-[#F9F9F6] mt-6">Order received.</h4>
                <p className="text-[#F9F9F6]/60 mt-3 text-sm leading-relaxed">
                  Reference <span className="text-[#D4AF37]" data-testid="order-ref">{done.order.ref}</span>. We've opened WhatsApp so you can send it straight to our team — we'll confirm availability and delivery.
                </p>
                <a href={done.whatsapp_url} target="_blank" rel="noopener noreferrer" data-testid="order-whatsapp-link"
                   className="mt-8 rounded-full bg-[#D4AF37] text-[#0A0A0A] px-8 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#F9F9F6] transition-colors">
                  Open WhatsApp
                </a>
              </div>
            ) : items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center px-8 text-center" data-testid="cart-empty">
                <ShoppingBag size={40} strokeWidth={1} className="text-[#F9F9F6]/30" />
                <p className="font-serif-e text-2xl text-[#F9F9F6]/70 mt-6">Your cart is empty.</p>
                <p className="text-[#F9F9F6]/40 text-sm mt-2">Browse the shop and add something beautiful.</p>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                  {items.map((i) => (
                    <div key={i.key} data-testid={`cart-item-${i.id}`} className="flex gap-4 border-b border-[#F9F9F6]/10 pb-5">
                      <img src={i.image} alt={i.name} className="w-16 h-20 object-cover border border-[#F9F9F6]/10" />
                      <div className="flex-1">
                        <p className="font-serif-e text-lg text-[#F9F9F6] leading-tight">{i.name}</p>
                        {i.option && <p className="text-[10px] uppercase tracking-[0.15em] text-[#D4AF37] mt-0.5">{i.option}</p>}
                        {i.selections && i.selections.map((s, si) => (
                          <p key={si} className="text-[10px] text-[#F9F9F6]/50 mt-0.5">{s}</p>
                        ))}
                        <p className="text-[#D4AF37] text-sm mt-1">R{i.price.toLocaleString()} <span className="text-[#F9F9F6]/40">/ {i.unit}</span></p>
                        <div className="flex items-center gap-3 mt-2">
                          <button onClick={() => setQty(i.key, i.qty - 1)} data-testid={`cart-minus-${i.id}`} aria-label="Decrease"
                                  className="w-7 h-7 rounded-full border border-[#F9F9F6]/20 flex items-center justify-center text-[#F9F9F6]/70 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                            <Minus size={12} />
                          </button>
                          <span data-testid={`cart-qty-${i.id}`} className="text-sm text-[#F9F9F6] w-4 text-center">{i.qty}</span>
                          <button onClick={() => setQty(i.key, i.qty + 1)} data-testid={`cart-plus-${i.id}`} aria-label="Increase"
                                  className="w-7 h-7 rounded-full border border-[#F9F9F6]/20 flex items-center justify-center text-[#F9F9F6]/70 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                            <Plus size={12} />
                          </button>
                        </div>
                      </div>
                      <p className="text-[#F9F9F6] text-sm font-semibold">R{(i.price * i.qty).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
                <form onSubmit={placeOrder} className="border-t border-[#F9F9F6]/10 px-6 py-5 space-y-3" data-testid="checkout-form">
                  {hasCupcakes && (
                    <div className="border border-[#D4AF37]/30 p-4" data-testid="inspo-upload-box">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-1">Cupcake inspiration</p>
                      <p className="text-[11px] text-[#F9F9F6]/50 mb-3">Upload a picture of the theme or style you'd like (optional). Quantity in your cart = number of cupcakes.</p>
                      {inspoPreview ? (
                        <div className="flex items-center gap-3">
                          <img src={inspoPreview} alt="Cupcake inspiration" data-testid="inspo-preview" className="w-16 h-16 object-cover border border-[#D4AF37]/40" />
                          <span className="text-xs text-[#D4AF37]">Uploaded ✓</span>
                        </div>
                      ) : (
                        <label data-testid="inspo-upload-label"
                               className="block w-full text-center rounded-full border border-dashed border-[#D4AF37]/50 text-[#D4AF37] py-3 text-[10px] uppercase tracking-[0.2em] cursor-pointer hover:bg-[#D4AF37]/10 transition-colors">
                          {uploading ? "Uploading…" : "Upload Inspo Picture"}
                          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="inspo-file-input" onChange={uploadInspo} />
                        </label>
                      )}
                    </div>
                  )}
                  <div className="flex justify-between items-baseline mb-2">
                    <span className="text-xs uppercase tracking-[0.2em] text-[#F9F9F6]/50">Total</span>
                    <span data-testid="cart-total" className="font-serif-e text-3xl text-[#D4AF37]">R{grandTotal.toLocaleString()}</span>
                  </div>
                  {selectedRate && (
                    <p data-testid="shipping-line" className="text-[10px] text-[#F9F9F6]/50 text-right -mt-2 mb-1">
                      incl. courier R{selectedRate.amount.toFixed(2)} — {selectedRate.label}
                    </p>
                  )}
                  <input required placeholder="Your name" value={form.customer_name} data-testid="checkout-name-input"
                         onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                         className="w-full bg-[#1A1A1A] border border-[#F9F9F6]/15 focus:border-[#D4AF37] px-4 py-3 text-sm text-[#F9F9F6] placeholder:text-[#F9F9F6]/30 transition-colors" />
                  <input required placeholder="Phone number" value={form.phone} data-testid="checkout-phone-input"
                         onChange={(e) => setForm({ ...form, phone: e.target.value })}
                         className="w-full bg-[#1A1A1A] border border-[#F9F9F6]/15 focus:border-[#D4AF37] px-4 py-3 text-sm text-[#F9F9F6] placeholder:text-[#F9F9F6]/30 transition-colors" />
                  <input required type="email" placeholder="Email address (for your confirmation)" value={form.email} data-testid="checkout-email-input"
                         onChange={(e) => setForm({ ...form, email: e.target.value })}
                         className="w-full bg-[#1A1A1A] border border-[#F9F9F6]/15 focus:border-[#D4AF37] px-4 py-3 text-sm text-[#F9F9F6] placeholder:text-[#F9F9F6]/30 transition-colors" />
                  <select required value={form.delivery} data-testid="checkout-delivery-select"
                          onChange={(e) => { setForm({ ...form, delivery: e.target.value }); setRates([]); setSelectedRate(null); setQuoteError(""); }}
                          className="w-full bg-[#1A1A1A] border border-[#F9F9F6]/15 focus:border-[#D4AF37] px-4 py-3 text-sm text-[#F9F9F6] transition-colors [color-scheme:dark]">
                    <option value="" disabled>How will you get your order?</option>
                    {DELIVERY_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  {isBobgo && (
                    <div className="border border-[#D4AF37]/30 p-4 space-y-3" data-testid="bobgo-quote-box">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Bobgo courier estimate</p>
                      <div className="flex gap-2">
                        <input placeholder="City" value={shipCity} data-testid="shipping-city-input"
                               onChange={(e) => setShipCity(e.target.value)}
                               className="flex-1 bg-[#0A0A0A] border border-[#F9F9F6]/15 focus:border-[#D4AF37] px-3 py-2.5 text-sm text-[#F9F9F6] placeholder:text-[#F9F9F6]/30 transition-colors" />
                        <input placeholder="Postal code" value={shipPostal} data-testid="shipping-postal-input"
                               onChange={(e) => setShipPostal(e.target.value)}
                               className="w-28 bg-[#0A0A0A] border border-[#F9F9F6]/15 focus:border-[#D4AF37] px-3 py-2.5 text-sm text-[#F9F9F6] placeholder:text-[#F9F9F6]/30 transition-colors" />
                      </div>
                      <button type="button" onClick={getQuote} disabled={quoting || !shipCity || !shipPostal} data-testid="get-quote-btn"
                              className="w-full rounded-full border border-[#D4AF37]/50 text-[#D4AF37] py-2.5 text-[10px] uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors disabled:opacity-40">
                        {quoting ? "Calculating…" : "Calculate Courier Cost"}
                      </button>
                      {quoteError && <p data-testid="quote-error" className="text-[#F9F9F6]/60 text-xs">{quoteError}</p>}
                      {rates.map((r) => (
                        <label key={r.id} data-testid={`rate-${r.id}`}
                               className={`flex items-center justify-between gap-3 border px-3 py-2.5 text-sm cursor-pointer transition-colors ${selectedRate?.id === r.id ? "border-[#D4AF37] text-[#F9F9F6]" : "border-[#F9F9F6]/15 text-[#F9F9F6]/70"}`}>
                          <span className="flex items-center gap-2">
                            <input type="radio" name="rate" checked={selectedRate?.id === r.id} onChange={() => setSelectedRate(r)} />
                            {r.label}
                          </span>
                          <span className="text-[#D4AF37]">R{r.amount.toFixed(2)}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  <input placeholder="Delivery notes / occasion (optional)" value={form.notes} data-testid="checkout-notes-input"
                         onChange={(e) => setForm({ ...form, notes: e.target.value })}
                         className="w-full bg-[#1A1A1A] border border-[#F9F9F6]/15 focus:border-[#D4AF37] px-4 py-3 text-sm text-[#F9F9F6] placeholder:text-[#F9F9F6]/30 transition-colors" />
                  {error && <p data-testid="checkout-error" className="text-red-400 text-xs">{error}</p>}
                  <button type="submit" disabled={placing} data-testid="checkout-submit-btn"
                          className="w-full rounded-full bg-[#D4AF37] text-[#0A0A0A] py-4 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#F9F9F6] transition-colors disabled:opacity-50">
                    {placing ? "Placing order…" : "Place Order via WhatsApp"}
                  </button>
                  <p className="text-[10px] text-[#F9F9F6]/40 text-center">Your order is sent to our team on WhatsApp — an 80% deposit confirms weekly meal prep, and payment is arranged on confirmation.</p>
                </form>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
